import type { ReactNode } from 'react'

import { Button } from './Button'
import { errorMeta, type ErrorMetaSource } from './errorMeta'
import { StatusMessage } from './StatusMessage'

/**
 * Не-«готовые» состояния запроса одинаково во всех окнах: загрузка, пусто, нет входа, нет доступа, недоступно, ошибка.
 * Один переключатель по `status` вместо копий в каждом пакете. Текст сервера — дословно (ADR-8), код и номер запроса
 * для отчёта собирает {@link errorMeta}; у ошибок, кроме загрузки и «пусто», есть кнопка копирования (StatusMessage).
 * Готовое состояние (`ready`) панель не рисует — данные показывает вызывающий.
 */

/** Ошибка запроса в форме, нужной панели; структурно подходит `ErrorView` клиентской среды выполнения. */
export interface QueryErrorLike extends ErrorMetaSource {
  message: string
  /** Вид ошибки транспорта; для `quietConnectionLoss` важны `network` и `timeout`. */
  kind?: string
}

/** Состояние запроса в форме, нужной панели; структурно подходит `QueryView` клиентской среды выполнения. */
export interface QueryViewLike {
  status: 'idle' | 'loading' | 'ready' | 'empty' | 'unauthorized' | 'forbidden' | 'unavailable' | 'error'
  error: QueryErrorLike | null
}

export interface QueryStateTitles {
  loading?: string
  forbidden?: string
  /** Строка или функция от ошибки: заголовок может зависеть от кода (1С недоступна / не приняла учётные данные). */
  unavailable?: string | ((error: QueryErrorLike | null) => string)
  error?: string
}

export interface QueryStatePanelProps {
  query: QueryViewLike
  /** Заголовок состояния «пусто». */
  emptyText: string
  /** «Повторить» для недоступно и ошибки; не задан — кнопки нет. */
  onRetry?: () => void
  /** Запись реестра действий для кнопки «Повторить» (UI-ACTIONS). */
  retryActionId?: string
  /** Ещё одно действие рядом с «Повторить» (например, «Начать выдачу заново»). */
  extra?: ReactNode
  /** Свои заголовки; по умолчанию «Загрузка…», «Нет доступа», «Служба недоступна», «Ошибка». */
  titles?: QueryStateTitles
  /** `idle` — запрос ещё не стартовал: `loading` рисует строку загрузки, `nothing` — ничего. */
  idle?: 'loading' | 'nothing'
  /** Служебную строку показывать только при номере запроса. */
  metaRequiresRequestId?: boolean
  /** Сбой связи (`network`, `timeout`) в «недоступно» показать строкой загрузки: сам сбой объявляет общее окно приложения. */
  quietConnectionLoss?: boolean
}

const DEFAULT_TITLES = {
  loading: 'Загрузка…',
  forbidden: 'Нет доступа',
  unavailable: 'Служба недоступна',
  error: 'Ошибка',
} as const

export function QueryStatePanel({
  query,
  emptyText,
  onRetry,
  retryActionId,
  extra,
  titles = {},
  idle = 'loading',
  metaRequiresRequestId = false,
  quietConnectionLoss = false,
}: QueryStatePanelProps) {
  const { error } = query
  const meta = errorMeta(error, { requestIdRequired: metaRequiresRequestId })
  const loadingTitle = titles.loading ?? DEFAULT_TITLES.loading
  const failure = {
    ...(error !== null ? { text: error.message } : {}),
    ...(meta !== undefined ? { meta } : {}),
  }
  const hasAction = onRetry !== undefined || extra !== undefined
  const action = hasAction ? (
    <>
      {onRetry !== undefined && (
        <Button variant="secondary" onClick={onRetry} {...(retryActionId !== undefined ? { actionId: retryActionId } : {})}>
          Повторить
        </Button>
      )}
      {extra}
    </>
  ) : undefined
  switch (query.status) {
    case 'idle':
      return idle === 'loading' ? <StatusMessage kind="loading" title={loadingTitle} /> : null
    case 'loading':
      return <StatusMessage kind="loading" title={loadingTitle} />
    case 'empty':
      return <StatusMessage kind="empty" title={emptyText} />
    case 'unauthorized':
      return <StatusMessage kind="unauthorized" title="Сессия завершена" text="Войдите снова, чтобы продолжить." />
    case 'forbidden':
      return <StatusMessage kind="forbidden" title={titles.forbidden ?? DEFAULT_TITLES.forbidden} {...failure} />
    case 'unavailable': {
      if (quietConnectionLoss && (error?.kind === 'network' || error?.kind === 'timeout')) {
        return <StatusMessage kind="loading" title={loadingTitle} />
      }
      const title = titles.unavailable ?? DEFAULT_TITLES.unavailable
      return (
        <StatusMessage
          kind="unavailable"
          title={typeof title === 'function' ? title(error) : title}
          {...failure}
          {...(action !== undefined ? { action } : {})}
        />
      )
    }
    case 'error':
      return (
        <StatusMessage
          kind="error"
          title={titles.error ?? DEFAULT_TITLES.error}
          {...failure}
          {...(action !== undefined ? { action } : {})}
        />
      )
    case 'ready':
      return null
  }
}
